import * as React from 'react';

import { enumPrinterStatus } from '@rematter/frontend-lib-api-client';
import { PrinterRecord } from '@rematter/modules/settings/types/sidecar';
import {
  Badge,
  Button,
  Col,
  Row,
  SearchField,
  ThemeColor,
  TinyTable,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCpu,
  LucideFileType,
  LucidePlus,
  LucidePrinter,
  LucideRadioTower,
  LucideWrench,
} from 'lucide-react';

import { usePrinterEventsSubscription } from '../../../_graphql/subscriptionPrinterEvents';
import * as state from '../../../_state/printersList.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { DemoPrinterCreateDialog } from './DemoPrinterCreateDialog';

import { useLibp2p } from '~/shared/providers/libp2p';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<PrinterRecord['status'], ThemeColor> = {
  [enumPrinterStatus.Discovered]: 'warning',
};

export const PrintersListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showDemoDialog, setShowDemoDialog] = React.useState(false);
  const { connections } = useLibp2p();
  const presenceMembers = useHardwarePresenceMembers();

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remotePrinters,
  );

  usePrinterEventsSubscription(undefined, {
    onEvent: () => {
      refetch();
    },
  });

  const allPrinters = data?.printers ?? [];

  const printers = React.useMemo(() => {
    if (!searchTerm.trim()) return allPrinters;
    const term = searchTerm.toLowerCase();
    return allPrinters.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.driverType.toLowerCase().includes(term) ||
        c.sidecar?.name.toLowerCase().includes(term),
    );
  }, [allPrinters, searchTerm]);

  return (
    <Col gap4 className="@PrintersListView h-full p-4">
      <Row gap2>
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t.hardwareView.printersPanel.search.placeholder}
          aria-label={t.hardwareView.printersPanel.search.ariaLabel}
          className="w-60 shrink-0"
        />
        <Button
          className="ml-auto"
          icon={<LucidePlus className="size-4" />}
          onPress={() => setShowDemoDialog(true)}
        >
          {t.hardwareView.demoCreateDialog.actions.addDemoPrinter}
        </Button>
      </Row>
      <DemoPrinterCreateDialog
        isOpen={showDemoDialog}
        onClose={() => setShowDemoDialog(false)}
        onSuccess={refetch}
      />
      <TinyTable<PrinterRecord>
        rowKey={(row) => row.id}
        isLoading={loadingState.isInitialLoading || isFetching}
        error={error}
        onRowClick={(row) =>
          setLocation(
            `/hardware/printers/${encodeURIComponent(row.sidecarId)}/${encodeURIComponent(row.id)}`,
          )
        }
        columns={[
          {
            key: 'name',
            label: t.hardwareView.printersPanel.columns.name,
            icon: LucidePrinter,
            width: 'auto',
            cell: (row) => (
              <div className="truncate font-medium">{row.name}</div>
            ),
          },
          {
            key: 'printerType',
            label: t.hardwareView.printersPanel.columns.printerType,
            icon: LucideFileType,
            width: 120,
            cell: (row) => (
              <div className="truncate">{row.printerType ?? '\u2013'}</div>
            ),
          },
          {
            key: 'driverType',
            label: t.hardwareView.printersPanel.columns.driverType,
            icon: LucideWrench,
            width: 180,
            cell: (row) => <div className="truncate">{row.driverType}</div>,
          },
          {
            key: 'status',
            label: t.hardwareView.printersPanel.columns.status,
            icon: LucideRadioTower,
            width: 'max-content',
            cell: (row) => {
              const isOnline = presenceMembers.has(row.id);
              const conn = connections.get(row.sidecarId);
              return (
                <Row gap1 className="items-center">
                  <Badge color={STATUS_COLORS[row.status]}>
                    {t.hardwareView.printersPanel.statusLabels[row.status]}
                  </Badge>
                  <Badge color={isOnline ? 'success' : 'neutral'}>
                    {isOnline
                      ? t.hardwareView.printersPanel.presenceLabels.online
                      : t.hardwareView.printersPanel.presenceLabels.offline}
                  </Badge>
                  {conn?.connected && (
                    <Badge
                      color={conn.type === 'direct' ? 'primary' : 'neutral'}
                    >
                      {conn.type === 'direct'
                        ? t.hardwareView.printersPanel.connectionLabels.direct
                        : t.hardwareView.printersPanel.connectionLabels.relayed}
                    </Badge>
                  )}
                </Row>
              );
            },
          },
          {
            key: 'sidecar',
            label: t.hardwareView.printersPanel.columns.sidecar,
            icon: LucideCpu,
            width: 220,
            cell: (row) => (
              <div className="truncate">{row.sidecar?.name ?? '\u2013'}</div>
            ),
          },
        ]}
        data={printers}
      />
    </Col>
  );
};
